/**
 * Clinical context profiles (ED / primary care / urgent care) used to tune
 * DDx scoring, sequencing, and stability behavior.
 */

export const CLINICAL_CONTEXT_PROFILE_IDS = Object.freeze({
  EMERGENCY_DEPARTMENT: 'emergency_department',
  PRIMARY_CARE: 'primary_care',
  URGENT_CARE: 'urgent_care',
});

export const CLINICAL_CONTEXT_PROFILES = Object.freeze({
  [CLINICAL_CONTEXT_PROFILE_IDS.EMERGENCY_DEPARTMENT]: Object.freeze({
    id: CLINICAL_CONTEXT_PROFILE_IDS.EMERGENCY_DEPARTMENT,
    label: 'Emergency Department',
    acuityPrior: 'high',
    optimizationTarget: 'rule_out',
    prevalenceWeight: 0.3,
    edgeStrengthWeight: 1.0,
    stabilityThreshold: 'low',
    stabilityWindow: 2,
    top3StabilityThreshold: 0.15,
    dispositionWindow: 'minutes_to_hours',
    requiredCantMissDispositionRate: 1.0,
    sequencingPolicy: 'vitals_first',
    rareDiseasePriority: 'deprioritized',
    treatableConditionBonus: 0.0,
    falseNegativeCost: 'catastrophic',
    falsePositiveCost: 'acceptable',
    implementationPhase: 1,
    status: 'active',
  }),
  [CLINICAL_CONTEXT_PROFILE_IDS.PRIMARY_CARE]: Object.freeze({
    id: CLINICAL_CONTEXT_PROFILE_IDS.PRIMARY_CARE,
    label: 'Primary Care',
    acuityPrior: 'low',
    optimizationTarget: 'rule_in',
    prevalenceWeight: 0.8,
    edgeStrengthWeight: 0.8,
    stabilityThreshold: 'high',
    stabilityWindow: 3,
    top3StabilityThreshold: 0.1,
    dispositionWindow: 'days_to_weeks',
    requiredCantMissDispositionRate: 0.95,
    sequencingPolicy: 'history_depth_first',
    rareDiseasePriority: 'elevated',
    treatableConditionBonus: 0.15,
    falseNegativeCost: 'serious',
    falsePositiveCost: 'meaningful',
    implementationPhase: 2,
    status: 'planned',
  }),
  [CLINICAL_CONTEXT_PROFILE_IDS.URGENT_CARE]: Object.freeze({
    id: CLINICAL_CONTEXT_PROFILE_IDS.URGENT_CARE,
    label: 'Urgent Care',
    acuityPrior: 'moderate',
    optimizationTarget: 'triage_and_treat',
    prevalenceWeight: 0.55,
    edgeStrengthWeight: 0.9,
    stabilityThreshold: 'moderate',
    stabilityWindow: 2,
    top3StabilityThreshold: 0.15,
    dispositionWindow: 'hours',
    requiredCantMissDispositionRate: 0.98,
    sequencingPolicy: 'hybrid',
    rareDiseasePriority: 'moderate',
    treatableConditionBonus: 0.1,
    falseNegativeCost: 'serious',
    falsePositiveCost: 'moderate',
    implementationPhase: 3,
    status: 'planned',
  }),
});

export const PREVALENCE_TIER_MULTIPLIERS = Object.freeze({
  common: 1.0,
  uncommon: 0.6,
  rare: 0.2,
  unknown: 0.6,
});

export const STABILITY_PARAMETERS = Object.freeze({
  [CLINICAL_CONTEXT_PROFILE_IDS.EMERGENCY_DEPARTMENT]: Object.freeze({
    stabilityWindow: 2,
    top3StabilityThreshold: 0.15,
    minimumInformationRequirements: Object.freeze({
      vitals: 'required',
      chiefComplaintCharacter: 'required',
      associatedSymptoms: 'required',
      pmh: 'preferred',
      exam: 'preferred',
      diagnostics: 'optional',
    }),
    cantMissDispositionRequired: true,
    maximumTransitionsBeforeAlert: 4,
  }),
  [CLINICAL_CONTEXT_PROFILE_IDS.PRIMARY_CARE]: Object.freeze({
    stabilityWindow: 3,
    top3StabilityThreshold: 0.1,
    minimumInformationRequirements: Object.freeze({
      vitals: 'preferred',
      chiefComplaintCharacter: 'required',
      associatedSymptoms: 'required',
      pmh: 'required',
      socialHistory: 'required',
      familyHistory: 'preferred',
      reviewOfSystems: 'required',
      exam: 'required',
      diagnostics: 'preferred',
    }),
    cantMissDispositionRequired: true,
    maximumTransitionsBeforeAlert: 7,
  }),
  [CLINICAL_CONTEXT_PROFILE_IDS.URGENT_CARE]: Object.freeze({
    stabilityWindow: 2,
    top3StabilityThreshold: 0.15,
    minimumInformationRequirements: Object.freeze({
      vitals: 'required',
      chiefComplaintCharacter: 'required',
      associatedSymptoms: 'required',
      pmh: 'preferred',
      exam: 'preferred',
      diagnostics: 'optional',
    }),
    cantMissDispositionRequired: true,
    maximumTransitionsBeforeAlert: 5,
  }),
});

/**
 * @param {string} profileId
 * @returns {object}
 */
export function getClinicalContextProfile(profileId) {
  return (
    CLINICAL_CONTEXT_PROFILES[profileId] ||
    CLINICAL_CONTEXT_PROFILES[CLINICAL_CONTEXT_PROFILE_IDS.EMERGENCY_DEPARTMENT]
  );
}

/**
 * @param {string} [profileId]
 * @returns {number}
 */
export function getStabilityMaxTransitionsBeforeAlert(profileId) {
  const cfg =
    STABILITY_PARAMETERS[profileId] ||
    STABILITY_PARAMETERS[CLINICAL_CONTEXT_PROFILE_IDS.EMERGENCY_DEPARTMENT];
  const m = Number(cfg?.maximumTransitionsBeforeAlert);
  return Number.isFinite(m) && m > 0 ? m : 4;
}
